// 3rd Party
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import {
  Div,
  FlexCol,
  FlexRow,
  Span,
  VisuallyHidden,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { LoadingOutlined, Select } from 'opencare-crown';
import RatingLarge from 'pages/common/components/RatingLarge/RatingLarge';
import RatingStars from 'pages/common/components/RatingStars/RatingStars';

import {
  combineAllReviews,
  getAggregateReviewData,
} from '../../../common/helpers/common-helpers';
import {
  selectDataClinic,
  selectDataClinicAverageRating,
  selectDataClinicProviders,
} from '../../connect/selectors';
import {
  getReviewStats,
  groupProviderIds,
} from '../../helpers/profile-helpers';
import {
  ProfileSubsectionBody,
  ProfileSubsectionDisclaimer,
  ProfileSubsectionHeader,
  ProfileSubsectionTitle,
} from '../../profile-styled-components';
import ReviewBreakdown from '../ReviewBreakdown/ReviewBreakdown';
import ReviewCard from '../ReviewCard/ReviewCard';
import ReviewFeedbackBlock from '../ReviewFeedbackBlock/ReviewFeedbackBlock';
import ReviewSourcesBlock from '../ReviewSourcesBlock/ReviewSourcesBlock';
import HappyTooth from '../svgs/HappyTooth';

// Other

import {
  AggregateReviewBreakdownContainer,
  AggregateReviewContentContainer,
  AggregateReviewDisclaimer,
  AggregateReviewSelectContainer,
  ClinicReviewsSubRatingsFlexRow,
  FlexContainer,
  HappyToothContainer,
  ReviewBreakdownList,
  ReviewContentContainer,
  ReviewsList,
  ReviewSourcesWrapper,
  ReviewsSelectWrapper,
  SeeMoreReviewsButton,
  StyledProfileSubsection,
  StyledSelect,
} from './styled-components';

const { Option } = Select;

const ClinicReviews = ({ clinicFromResults, isInResultsPage }) => {
  const averageRating = useSelector(selectDataClinicAverageRating);
  const clinicProviders = useSelector(selectDataClinicProviders);

  let clinic = useSelector(selectDataClinic);
  if (clinicFromResults) clinic = clinicFromResults;

  // Clinic sub-rating options descriptor.
  const subRatingOptions = [
    {
      value: clinic.aggregateSubratings?.staff?.score,
      title: 'Staff',
    },
    {
      value: clinic.aggregateSubratings?.treatment?.score,
      title: 'Quality of Treatment',
    },
    {
      value: clinic.aggregateSubratings?.equipment?.score,
      title: 'Equipment',
    },
  ].filter(option => option.value);

  const clinicReviews = combineAllReviews(clinic)
    .filter(review => review.description?.length)
    .sort((a, b) => {
      // sort by rating then date then length
      if (a.overallRating > b.overallRating) return -1;
      if (a.overallRating < b.overallRating) return 1;

      if (a.createdAt > b.createdAt) return -1;
      if (a.createdAt < b.createdAt) return 1;

      if (a.description.length > b.description.length) return -1;
      if (a.description.length < b.description.length) return 1;

      return 0;
    });
  const providerIds = groupProviderIds(clinicProviders);
  const reviewStatsAll = getReviewStats(providerIds, clinicReviews);

  const [isLoading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [activeReviews, setActiveReviews] = useState(clinicReviews);
  const [displayedReviews, setDisplayedReviews] = useState(
    clinicReviews.slice(0, 10),
  );
  const [reviewAverageRating, setReviewAverageRating] = useState(averageRating);
  const [reviewStats, setReviewStats] = useState(reviewStatsAll);
  const [iterator, setIterator] = useState(1);

  const aggregateReviews = getAggregateReviewData({
    clinicId: clinic.id,
    opencareReviews: clinicReviews,
  });

  // Refresh reviews when user selects a different clinic from results page
  useEffect(() => {
    if (isInResultsPage) {
      setDisplayedReviews(clinicReviews.slice(0, 10));
    }
  }, [clinicFromResults]);

  useEffect(() => {
    if (selectedDoctor && selectedDoctor !== 'all') {
      // when selecting a specific provider in the reviews section, select it from the list of all providers
      const selectedDoctorReviews = _.find(
        clinicProviders,
        provider => provider.doctor.id === selectedDoctor,
      );

      // check if the provider has reviews, and populate those into state
      // if the provider doesn't have reviews yet, clear state so the review components dont render
      if (
        selectedDoctorReviews.doctor.reviews.length > 0
        && selectedDoctorReviews.doctor.aggregateReviewData
      ) {
        setActiveReviews(selectedDoctorReviews.doctor.reviews);
        setDisplayedReviews(selectedDoctorReviews.doctor.reviews.slice(0, 10));
        setReviewAverageRating(
          selectedDoctorReviews.doctor.aggregateReviewData.averageRating,
        );

        const selectedDoctorReviewStats = getReviewStats(
          selectedDoctorReviews.doctor.id,
          selectedDoctorReviews.doctor.reviews,
        );
        setReviewStats(selectedDoctorReviewStats);
        setIterator(1);
      } else {
        setActiveReviews([]);
        setDisplayedReviews([]);
        setReviewAverageRating(0);
        setReviewStats({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, total: 0 });
        setIterator(1);
      }
    } else {
      // if the user selects all providers, set state to display all provider reviews
      setActiveReviews(clinicReviews);
      setDisplayedReviews(clinicReviews.slice(0, 10));
      setReviewAverageRating(averageRating);
      setReviewStats(reviewStatsAll);
    }
  }, [selectedDoctor]);

  useEffect(() => {
    const incrementReviews = 10 * iterator;
    setDisplayedReviews(activeReviews.slice(0, incrementReviews));
    setLoading(false);
  }, [iterator]);

  const renderReviewBreakdown = () => {
    if (!reviewStats || !reviewStats.totals) return null;
    const breakdowns = [];
    for (let i = 5; i > 0; i -= 1) {
      breakdowns.push(
        <li key={i}>
          <ReviewBreakdown
            numberOfReviews={reviewStats.totals[i]}
            rating={i}
            total={reviewStats.totals.total}
          />
        </li>,
      );
    }
    return <ReviewBreakdownList>{breakdowns}</ReviewBreakdownList>;
  };

  const handleChange = e => {
    setSelectedDoctor(e);
  };

  const ReviewsSelect = () => {
    if (!clinicProviders || clinicProviders.length === 1) {
      return null;
    }

    return (
      <StyledSelect
        value={selectedDoctor}
        defaultValue="all"
        onChange={handleChange}
        disabled={!clinicReviews}
      >
        <Option key="all" value="all">
          All reviews
        </Option>
        {clinicProviders.map(provider => (
          <Option key={provider.doctor.id} value={provider.doctor.id}>
            {provider.doctor.name}
          </Option>
        ))}
      </StyledSelect>
    );
  };

  const ReviewsBreakdown = () => (
    <ReviewContentContainer
      width="60%"
      as="figure"
      aria-label="Review breakdown"
    >
      {renderReviewBreakdown()}
    </ReviewContentContainer>
  );

  const Reviews = () => {
    if (!activeReviews || activeReviews.length === 0) {
      return (
        <Div margin="0 0 40px 0">
          <FlexRow align="center">
            <HappyToothContainer>
              <HappyTooth height="50px" width="50px" />
            </HappyToothContainer>
            <Span margin="0 0 0 15px">
              This provider hasn't received any reviews from patients yet!
            </Span>
          </FlexRow>
        </Div>
      );
    }

    return (
      <ReviewsList>
        {displayedReviews.map((review, i) => (
          <li key={review.id}>
            <ReviewCard
              review={review}
              index={i}
              finalReview={i === displayedReviews.length - 1}
            />
          </li>
        ))}
        {displayedReviews.length < activeReviews.length
          ? (
            <SeeMoreReviewsButton
              background={colors.blue}
              color={colors.white}
              onClick={() => {
                setLoading(true);
                setIterator(iterator + 1);
              }}
            >
              {isLoading && <LoadingOutlined />}
              See more reviews
            </SeeMoreReviewsButton>
          )
          : null}
      </ReviewsList>
    );
  };

  return (
    <>
      <div id="optimizely-ar-clinic-reviews-original">
        <VisuallyHidden>
          <h2>Customer reviews</h2>
        </VisuallyHidden>

        {subRatingOptions.length > 0
          && (
            <ClinicReviewsSubRatingsFlexRow>
              {subRatingOptions.map(subRatingOption => (
                <FlexCol style={{ gap: '8px' }} key={subRatingOption.title}>
                  <FlexRow
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      textWrap: 'nowrap',
                    }}
                  >
                    {subRatingOption.title}
                  </FlexRow>
                  <FlexRow style={{ gap: '8px' }} flexWrap="nowrap">
                    <RatingStars
                      averageRating={subRatingOption.value}
                      sideLength="20px"
                    />
                    <FlexRow style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {subRatingOption.value.toFixed(1)}
                    </FlexRow>
                  </FlexRow>
                </FlexCol>
              ))}
            </ClinicReviewsSubRatingsFlexRow>
          )
        }

        {!isInResultsPage
          && (
            <>
              <ReviewsSelectWrapper>
                <ReviewsSelect />
              </ReviewsSelectWrapper>
              {activeReviews && activeReviews.length > 0
                ? (
                  <FlexContainer margin="0 0 70px 0">
                    <ReviewContentContainer width="40%">
                      <ReviewFeedbackBlock
                        reviews={activeReviews}
                        averageRating={reviewAverageRating}
                      />
                    </ReviewContentContainer>
                    <ReviewsBreakdown />
                  </FlexContainer>
                )
                : null}
            </>
          )
        }
        <StyledProfileSubsection>
          {!isInResultsPage
            && (
              <ProfileSubsectionHeader>
                <ProfileSubsectionTitle
                  fontSize="18px"
                  aria-label={`${
                    activeReviews.length
                  } Detailed Reviews by Verified Patients`}
                >
                  {`${activeReviews.length} Detailed Reviews`}
                </ProfileSubsectionTitle>
                <ProfileSubsectionDisclaimer>
                  All reviews are the subjective opinions of a patient and not of
                  Opencare.
                </ProfileSubsectionDisclaimer>
              </ProfileSubsectionHeader>
            )
          }
          <ProfileSubsectionBody>
            <Reviews />
          </ProfileSubsectionBody>
        </StyledProfileSubsection>
      </div>
      {/* Aggregated reviews experiment version */}
      <Div display="none" id="optimizely-ar-clinic-reviews-experiment">
        <StyledProfileSubsection>
          <ProfileSubsectionHeader>
            <ProfileSubsectionTitle fontSize="18px">
              Customer Review Summary
            </ProfileSubsectionTitle>
          </ProfileSubsectionHeader>
          <ProfileSubsectionBody>
            {activeReviews && activeReviews.length > 0
              ? (
                <FlexContainer margin="0 0 70px 0">
                  <AggregateReviewContentContainer>
                    <RatingLarge
                      averageRating={aggregateReviews.rating}
                      reviewsCount={aggregateReviews.count}
                      sourcesCount={aggregateReviews.sources}
                      profileReviews="true"
                      textAlign="left"
                      inlineRatingStars
                    />
                    <ReviewSourcesWrapper>
                      <ReviewSourcesBlock
                        clinicId={clinic.id}
                        opencareReviews={clinicReviews}
                      />
                    </ReviewSourcesWrapper>
                  </AggregateReviewContentContainer>
                </FlexContainer>
              )
              : null}
          </ProfileSubsectionBody>
        </StyledProfileSubsection>
        <StyledProfileSubsection>
          <ProfileSubsectionHeader>
            <ProfileSubsectionTitle fontSize="18px">
              {`${activeReviews.length} Verified Opencare Reviews`}
            </ProfileSubsectionTitle>
          </ProfileSubsectionHeader>
          <ProfileSubsectionBody>
            <AggregateReviewBreakdownContainer>
              {clinicProviders && clinicProviders.length > 1
                && (
                  <AggregateReviewSelectContainer>
                    <ReviewsSelect />
                  </AggregateReviewSelectContainer>
                )
              }
              <AggregateReviewDisclaimer>
                All reviews are the subjective opinions of a patient and not of
                Opencare.
              </AggregateReviewDisclaimer>
            </AggregateReviewBreakdownContainer>
            <ReviewsBreakdown />
            <Reviews />
          </ProfileSubsectionBody>
        </StyledProfileSubsection>
      </Div>
    </>
  );
};

ClinicReviews.propTypes = {
  clinicFromResults: PropTypes.object,
  isInResultsPage: PropTypes.bool,
};

export default ClinicReviews;
